import React, { useState, useEffect } from "react";
import deviceProfile from "images/device.png";
import TrashIcon from "@mui/icons-material/DeleteRounded";
import DatePicker from "react-datepicker";
import toast from "react-hot-toast";
import {
    generateDeepLink as generateDeepLinkApi, unpairDevice, sendInvite, generateTemporaryLink, fetchOrganizationDevices, JoinKonnectDevices, UpdateKonnektContact,
    ImportKonnektContacts, fetchDeviceSettings, updateDeviceSetting
} from 'js/Api/index';
import PaginatedItemsTable from "js/Component/PaginatedItemsTable";
import EditIcon from "@mui/icons-material/EditRounded";
import { Button } from '../../../../Component/CommonFormEle';
import { hasValue } from "../../../../helpers/common_functions";
import SegmentedToggle from "js/Component/AddressBook/SegmentedToggle";
import AddressBookView from "js/Component/AddressBook/AddressBookView";
import SharedAddressBooksView from "js/Component/AddressBook/SharedAddressBooksView";
import ContactsReorderModal from "js/Component/ContactsReorderModal";

function ContactsTab({ user, fetchLinkedDevices, setreload_invite, linkedDevices, setLinkedDevices, selectedOrg, OpneInviteModel }) {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteLink, setInviteLink] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteDevice, setDeleteDevice] = useState(null);
    const [emailAddresses, setEmailAddresses] = useState("");
    const [inviteResults, setInviteResults] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showTempLinkModal, setShowTempLinkModal] = useState(false);
    const [expiryDate, setExpiryDate] = useState(new Date());
    const [tempLink, setTempLink] = useState("");
    const [showTempLink, setShowTempLink] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredDevices, setFilteredDevices] = useState([]);
    const [isKonnektOrg, setisKonnektOrg] = useState(null);

    // Address book only applies to CareWindow devices. Detect konnekt by either
    // the selected org or the device type / konnekt device id.
    const isKonnektContext =
        !!isKonnektOrg ||
        user?.deviceType === "konnekt-device" ||
        !!user?.konnektDeviceId;

    const [addressBookMode, setAddressBookMode] = useState("legacy");

    useEffect(() => {
        fetchLinkedDevices();
    }, []);

    useEffect(() => {
        if (selectedOrg?.code === "konnekt") {
            setisKonnektOrg(true);
            fetchKonnektDevices();
        }
    }, [selectedOrg]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredDevices(linkedDevices);
        } else {
            const filtered = linkedDevices.filter(device =>
                device.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredDevices(filtered);
        }
    }, [searchQuery, linkedDevices]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest('.dropdown')) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const getDeepLink = async () => {
        const result = await generateDeepLinkApi(user.id);

        if (result.success) {
            setInviteLink(result.data.url);
            return result.data.url;
        } else {
            console.log("Failed to generate deep link:", result.error);
        }
        return "";
    };

    const handleUnpairDevice = async (deviceId) => {
        const result = await unpairDevice(deviceId);

        if (result.success) {
            setLinkedDevices(
                linkedDevices.filter((device) => device.id !== deviceId)
            );
            toast.success(result.message);
            fetchLinkedDevices();
        } else {
            console.log("Failed to unpair device:", result.error);
            toast.error(result.error);
        }
    };
    const handleDeleteDeviceClick = (device) => {
        setDeleteDevice(device);
        setShowDeleteModal(true);
    };

    const handleDeleteDeviceCancel = () => {
        setShowDeleteModal(false);
        setDeleteDevice(null);
    };

    const handleUnpairClick = () => {
        if (deleteDevice) {
            handleUnpairDevice(deleteDevice.id);
            setShowDeleteModal(false);
            setDeleteDevice(null);
        }
    };

    const handleInviteModal = () => {
        setShowInviteModal(true);
    };

    const handleSendClick = async (event) => {
        event.preventDefault();
        setIsSending(true);
        setInviteResults([]);

        let inviteURL = await getDeepLink();

        const emailAddressArray = emailAddresses
            .split(/[,;\n]/)
            .map((email) => email.trim())
            .filter((email) => email !== "");
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        for (const email of emailAddressArray) {
            if (emailRegex.test(email)) {
                const result = await sendInvite(user.id, email, inviteURL);

                setInviteResults((prevResults) => [
                    ...prevResults,
                    {
                        email,
                        success: result.success,
                        message: result.success ? result.message : result.error
                    },
                ]);
            } else {
                setInviteResults((prevResults) => [
                    ...prevResults,
                    { email, success: false, message: "Invalid email address" },
                ]);
            }
        }

        setIsSending(false);
    };

    const handleCopyInviteLink = async () => {
        let inviteURL = await getDeepLink();

        navigator.clipboard.writeText(
            selectedOrg.name + ` has invited you to join ${user.name}'s CareWindow family group. Tap on this link on your smartphone or tablet to download the CareWindow app and join the group.\n${inviteURL}`
        );
        toast.success("Invite link copied to clipboard!");
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleTempLinkModal = () => {
        setShowTempLinkModal(true);
        setShowTempLink(false);
        setTempLink("");
    };

    const ImportKonnectContacts = async () => {
        const result = await ImportKonnektContacts(user.id);
        if (result.success) {
            setreload_invite(false)
            toast.success("Contacts are imported successfully!");
        }
    };

    const handleGenerateLink = async () => {
        const result = await generateTemporaryLink(user.id, expiryDate.toISOString());

        if (result.success) {
            setTempLink(result.data.url);
            setShowTempLink(true);
            toast.success("Temporary link generated successfully!");
        } else {
            toast.error(result.error || "Failed to generate temporary link. Please try again.");
        }
    };

    const handleCopyTempLink = () => {
        navigator.clipboard.writeText(tempLink);
        toast.success("Temporary link copied to clipboard!");
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    //#region Konnect

    const [showLinkKonnekt, setshowLinkKonnekt] = useState(false);
    const handleKonnektDeviceAdd = () => {
        setshowLinkKonnekt(true);
    };

    const [konnect_devices, setkonnect_devices] = useState([]);
    const fetchKonnektDevices = async () => {
        const response = await fetchOrganizationDevices(selectedOrg?.code);
        if (response.success) {
            setkonnect_devices(response.data.filter(a => a.id !== user.id));
        }
    };

    const handleCancel = () => {
        setSelectedItems([]);
        setshowLinkKonnekt(false);
        setSearchQuery("");

    };

    const filteredItems = konnect_devices.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.serialNo && item.serialNo.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const [selectedItems, setSelectedItems] = useState([]);
    const handleItemSelect = (itemId) => {
        setSelectedItems((prevSelectedItems) =>
            prevSelectedItems.includes(itemId)
                ? prevSelectedItems.filter((id) => id !== itemId)
                : [...prevSelectedItems, itemId]
        );
    };

    const [showKonnectMergeModel, setshowKonnectMergeModel] = useState(false);

    const handleConfirm = () => {
        setshowKonnectMergeModel(true);
    };

    const handleGroupKonnktIdConfirm = async () => {
        const requestBody = {
            id: user.id,
            devices: selectedItems,
        };

        const result = await JoinKonnectDevices(requestBody);
        if (result.success) {
            alert("Konnekt to family group added.")
            window.location.reload();
        } else {
            console.log("Failed to merge family");
            toast.error(result.error);
        }

    };

    const [showKonnectEditModel, setshowKonnectEditModel] = useState(false);
    const [isManager, setisManager] = useState(false);
    const [edit_id, setedit_id] = useState();
    const [updateShortName, setupdateShortName] = useState();
    const handleEditKonnectContact = (edit_id) => {
        let edit_item = linkedDevices.find((device) => device.id === edit_id);
        setupdateShortName(edit_item?.shortName);
        setedit_id(edit_id);
        setisManager(edit_item?.isLeader || false);

        setshowKonnectEditModel(true);
    };

    const handleUpdateContact = async () => {
        const requestBody = {
            id: edit_id,
            shortName: updateShortName,
            manager: isManager
        };

        const result = await UpdateKonnektContact(requestBody);
        if (result.success) {
            toast.success("Contact updated.");
            fetchLinkedDevices();
            setshowKonnectEditModel(false);
        } else {
            console.log("Failed to update contact.");
            toast.error(result.error);
        }
    };

    //#endregion

    //#region contact odering

    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderSetting, setOrderSetting] = useState(null);
    const [maxContacts, setmaxContactsSetting] = useState(8);
    const [orderList, setOrderList] = useState([]);
    const [savingOrder, setSavingOrder] = useState(false);

    //useEffect(() => {
    //    loadSettings();
    //}, []);

    const loadSettings = async (openpopup) => {

        try {
            const result = await fetchDeviceSettings(user.id);
            if (result.success) {

                let maxContactValue = result.data.filter(a => ["settings.max.contacts_per_page"].includes(a.friendlyName))[0].value;
                if (hasValue(maxContactValue))
                    setmaxContactsSetting(maxContactValue)

                let parsedIds = [];
                let contact_setting = result.data.filter(a => ["settings.device_contact_order"].includes(a.friendlyName))[0];
                setOrderSetting(contact_setting)
                let settingvalue = contact_setting.value;
                try {
                    const parsed = JSON.parse(settingvalue);
                    if (Array.isArray(parsed)) {
                        parsedIds = parsed.map((x) => String(x));
                    }
                } catch (e) {
                    console.error("Failed to parse Order Contact list in row:", e);
                }

                if ((!parsedIds || parsedIds.length === 0) && linkedDevices && linkedDevices.length > 0) {
                    parsedIds = linkedDevices.map((d) => String(d.id));
                }

                setOrderList(parsedIds.filter(element => linkedDevices.map(user => user.id).includes(element)));

                const labels = parsedIds.map((id) => {
                    const dev = linkedDevicesMap[id];
                    return dev?.shortName || dev?.name || id;
                });

                let summary = "Not configured";
                if (labels.length > 0) {
                    const display = labels.slice(0, 4).join(", ");
                    summary =
                        labels.length > 4
                            ? `${display} … (${labels.length} contacts)`
                            : display;
                }

                if (openpopup) {
                    setShowOrderModal(true);
                }
            }
        } catch (err) {
            console.error("Error fetching device settings:", err);
            toast.error("Failed to load device settings");
        } finally {

        }
    };

    const linkedDevicesMap = React.useMemo(() => {
        const map = {};
        (linkedDevices || []).forEach((d) => {
            if (d && d.id != null) {
                map[String(d.id)] = d;
            }
        });
        return map;
    }, [linkedDevices]);

    const handleSaveOrder = async (orderedIds) => {
        try {
            setSavingOrder(true);
            const valueStr = JSON.stringify(orderedIds || []);
            const result = await updateDeviceSetting(
                orderSetting,
                user.id,
                valueStr
            );
            if (result.success) {
                setOrderList(orderedIds);
                toast.success("Order Contact list updated");
                setShowOrderModal(false);
            } else {
                toast.error(result.error || "Failed to update Order Contact list");
            }
        } catch (error) {
            console.error("Error updating Order Contact list:", error);
            toast.error("Failed to update Order Contact list");
        } finally {
            setSavingOrder(false);
        }
    };

    const reorderItems = React.useMemo(() => {
        return orderList.map((id) => {
            const dev = linkedDevicesMap[id];
            return {
                id,
                label: dev?.shortName || dev?.name || String(id),
                avatarSrc: dev?.profilePicture
                    ? `data:image/png;base64,${dev.profilePicture}`
                    : null,
            };
        });
    }, [orderList, linkedDevicesMap]);

    //#endregion

    const contactsToolbar = (
        <>
            {isKonnektOrg ?
                <div>
                    <button onClick={handleKonnektDeviceAdd} type="button" className="btn cur-p btn-outline-dark" style={{ marginBottom: "20px" }} > Add Konnekt Device </button>
                    &nbsp;&nbsp;
                    <button onClick={OpneInviteModel} type="button" className="btn cur-p btn-outline-dark" style={{ marginBottom: "20px" }} > Invite Konnekt Contact </button>
                    &nbsp;&nbsp;
                    <button onClick={handleTempLinkModal} type="button" className="btn cur-p btn-outline-dark" style={{ marginBottom: "20px" }} > Share Temporary Link </button>
                    &nbsp;&nbsp;
                    <button onClick={ImportKonnectContacts} type="button" className="btn cur-p btn-outline-dark" style={{ marginBottom: "20px" }} > Import Konnekt Contacts </button>
                </div>
                :
                <div style={{ display: "block" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div className="dropdown" style={{ marginBottom: "20px", position: "relative" }}>
                            <button className={`btn btn-dark dropdown-toggle ${isDropdownOpen ? 'show' : ''}`} type="button" onClick={toggleDropdown} aria-expanded={isDropdownOpen} style={{ fontWeight: 700, width: "15rem" }} >
                                Invite Family Members
                            </button>

                            {isDropdownOpen && (
                                <ul className="dropdown-menu " style={{
                                    display: 'block', position: 'absolute', top: '100%', left: 0, zIndex: 1000, backgroundColor: '#fff',
                                    border: '1px solid rgba(0,0,0,.15)', borderRadius: '0.25rem', padding: '0.5rem 0', minWidth: '10rem'
                                }} >
                                    <li>
                                        <button className="dropdown-item" onClick={() => { handleInviteModal(); setIsDropdownOpen(false); }}
                                            style={{
                                                display: 'block', width: '100%', padding: '0.25rem 1rem', clear: 'both', fontWeight: 400,
                                                textAlign: 'left', border: 0, backgroundColor: 'transparent', cursor: 'pointer'
                                            }} > By Email </button>
                                    </li>
                                    <li>
                                        <button className="dropdown-item" onClick={() => { handleCopyInviteLink(); setIsDropdownOpen(false); }}
                                            style={{
                                                display: 'block', width: '100%', padding: '0.25rem 1rem', clear: 'both',
                                                fontWeight: 400, textAlign: 'left', border: 0, backgroundColor: 'transparent', cursor: 'pointer'
                                            }} >
                                            Copy invitation to clipboard
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>

                        <button onClick={handleTempLinkModal} type="button" className="btn cur-p btn-outline-dark" style={{ marginBottom: "20px" }} > Share Temporary Link </button>
                    </div>
                </div>
            }

            <div className="form-group" style={{ marginBottom: "20px", width: "50%" }}>
                <div className="input-group">
                    <input type="text" className="form-control" placeholder="Search contacts by name..." value={searchQuery} onChange={handleSearchChange} />
                </div>
            </div>
        </>
    );

    const legacyTable = (
        <>
            <table className="table table-hover">
                <caption style={{ captionSide: "top", textAlign: "center", marginRight: "160px", }} >
                    <h3>Contacts
                        {isKonnektOrg ? null :
                            <>
                                <Button onClick={() => loadSettings(true)} variant="black" style={{ float: "right" }}>
                                    Reorder Contacts
                                </Button>
                            </>
                        }
                    </h3>
                </caption>
                <thead>
                    <tr>
                        {isKonnektOrg ?
                            <>
                                <th>Short Name</th>
                                <th>Displayed Name</th>
                                <th>Skype Id</th>
                                <th>Actions</th>
                            </>
                            :
                            <>
                                <th>Contact Name</th>
                                <th>Actions</th>
                            </>
                        }
                    </tr>
                </thead>
                <tbody>
                    {filteredDevices.map((device) => (
                        <tr key={device.id}>
                            {isKonnektOrg ?
                                <>
                                    <td style={{ verticalAlign: "middle" }}>{device?.shortName}</td>
                                    <td style={{ verticalAlign: "middle" }}>
                                        {device.profilePicture ? (
                                            <img className="user-photo" src={`data:image/png;base64, ${device.profilePicture}`} alt={device.name} />
                                        ) : (
                                            <img className="user-photo" src={deviceProfile} alt="Default profile" />
                                        )}
                                        {device.name}
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>{device?.konnektDeviceId}</td>
                                </>
                                :
                                <>
                                    <td>
                                        {device.profilePicture ? (
                                            <img className="user-photo" src={`data:image/png;base64, ${device.profilePicture}`} alt={device.name} />
                                        ) : (
                                            <img className="user-photo" src={deviceProfile} alt="Default profile" />
                                        )}
                                        {device.name}
                                    </td>
                                </>
                            }
                            <td style={{ verticalAlign: "middle" }}>
                                {isKonnektOrg && <EditIcon onClick={() => { handleEditKonnectContact(device.id); }} className="iconButtonStyle" sx={{ marginRight: "5px" }} />}
                                <TrashIcon className="iconButtonStyle" onClick={() => handleDeleteDeviceClick(device)} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );

    const legacyModals = (
        <>
            {showDeleteModal && (
                <div className="modal fade show" style={{ display: "block" }} aria-modal="true" role="dialog" >
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title">Delete Device</h1>
                                <button type="button" className="close-button" onClick={handleDeleteDeviceCancel} aria-label="Close" >  <span aria-hidden="true">&times;</span> </button>
                            </div>
                            <div className="modal-body">
                                <p> Are you sure you want to unenroll{" "} <strong>{deleteDevice?.name}</strong>? </p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleDeleteDeviceCancel} > Cancel </button>
                                <button type="button" className="btn btn-danger" onClick={handleUnpairClick} > Delete </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showInviteModal && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "400px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Invite Contacts</h1>
                                <button className="close-button" onClick={() => setShowInviteModal(false)} > &times; </button>
                            </div>
                            <form className="contact-form" onSubmit={handleSendClick}>
                                <div className="mb-3">
                                    <p>You may enter up to 6 email addresses, one per line.</p>
                                    <strong>
                                        <label htmlFor="ContactsEmailAddress" className="form-label" style={{ marginTop: "20px" }} > Email Addresses </label>
                                    </strong>
                                    <textarea className="form-control" id="ContactsEmailAddress" rows="8" placeholder={`joe.baker@example.com\nfran.baker@example.com`}
                                        value={emailAddresses} onChange={(e) => setEmailAddresses(e.target.value)} ></textarea>
                                </div>
                                {inviteResults.length > 0 && (
                                    <div className="mb-3">
                                        <h5>Invite Results:</h5>
                                        <ul>
                                            {inviteResults.map((result, index) => (
                                                <li key={index} className={result.success ? "text-success" : "text-danger"} >
                                                    {result.email}: {result.message}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="modal-footer">
                                    <button type="button" className="btn cur-p btn-secondary" onClick={() => setShowInviteModal(false)} disabled={isSending} > Cancel </button>
                                    <button type="submit" className="btn cur-p btn-primary" disabled={isSending} > {isSending ? "Sending..." : "Send"} </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showTempLinkModal && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "500px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title" style={{ fontWeight: 700 }}>Share Temporary Link</h1>
                                <button className="close-button" onClick={() => setShowTempLinkModal(false)} > &times; </button>
                            </div>
                            <div className="modal-body">
                                <p style={{ marginBottom: "30px", color: "black" }}>
                                    Share this link with a doctor, nurse, carer or other trusted person
                                    to allow them call {user.name}'s CareWindow device from their web browser.
                                </p>
                                <div className="form-group" style={{ marginBottom: "30px" }}>
                                    <label htmlFor="expiryDate" style={{ display: "block", marginBottom: "10px", fontWeight: "400", color: "black" }} > Expiry date </label>
                                    <DatePicker selected={expiryDate} onChange={(date) => setExpiryDate(date)} className="form-control" dateFormat="dd/MM/yyyy"
                                        minDate={new Date()} style={{ width: "100%" }} />
                                </div>
                                <button className="btn btn-dark " onClick={handleGenerateLink} style={{ marginBottom: "30px", width: "150px" }} > Generate Link </button>

                                {showTempLink && (
                                    <div className="form-group">
                                        <label htmlFor="tempLink" style={{ display: "block", marginBottom: "10px", fontWeight: "400", color: "black" }} > Link </label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value={tempLink} readOnly style={{ marginRight: "10px" }} />
                                            <button className="btn btn-outline-dark" type="button" onClick={handleCopyTempLink} style={{ width: "100px" }} > Copy </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showLinkKonnekt && (
                <div className="modal" tabIndex={"-1"} role="dialog" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ height: "98vh", minWidth: "700px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Add another Konnekt to family group</h1>
                                <button className="close-button" onClick={handleCancel}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <input type="text" className="form-control" placeholder="Search by CareWindow User or Serial" value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)} />
                                </div>

                                <PaginatedItemsTable items={filteredItems} selectedItems={selectedItems} onItemSelect={handleItemSelect} itemType={"device"} itemsPerPage={10} />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={selectedItems.length === 0} > Confirm </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showKonnectMergeModel && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "400px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Merge Konnekt Devices</h1>
                                <button className="close-button" onClick={() => setshowKonnectMergeModel(false)} > &times; </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to merge selected devices to single family group?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn cur-p btn-secondary" onClick={() => setshowKonnectMergeModel(false)} > Cancel </button>
                                <button type="button" className="btn cur-p btn-primary" onClick={handleGroupKonnktIdConfirm} > Confirm </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showKonnectEditModel && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: "400px" }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Edit Contact</h1>
                                <button className="close-button" onClick={() => setshowKonnectEditModel(false)} > &times; </button>
                            </div>
                            <div className="modal-body">
                                <div class="form-group">
                                    <label class="col-form-label" >Short Name</label>
                                    <input type="text" class="form-control" maxLength="50" required value={updateShortName} onChange={(e) => setupdateShortName(e.target.value)} />
                                </div>
                                <div class="form-group">
                                    <label>
                                        <input type="radio" name="userType" id="Family_manager" value="Family_manager" checked={isManager}
                                            onChange={(e) => { setisManager(e.target.checked) }}
                                        />
                                        &nbsp; Family manager
                                        &nbsp; <input type="radio" name="userType" id="normal_user" value="normal_user" checked={isManager === false} onChange={(e) => { setisManager(!e.target.checked) }} />
                                        &nbsp; Normal user
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn cur-p btn-secondary" onClick={() => setshowKonnectEditModel(false)} > Cancel </button>
                                <button type="button" className="btn cur-p btn-primary" onClick={handleUpdateContact} > Update </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ContactsReorderModal
                open={showOrderModal}
                items={reorderItems}
                maxPerPage={maxContacts}
                saving={savingOrder}
                onClose={() => setShowOrderModal(false)}
                onSave={handleSaveOrder}
            />
        </>
    );

    return (
        <div>
            {contactsToolbar}
            {!isKonnektContext && (
                <div style={{ marginBottom: 16 }}>
                    <SegmentedToggle
                        options={[
                            { value: "legacy", label: "Old Contacts" },
                            { value: "home", label: "Home Page Contacts" },
                            { value: "shared", label: "Shared Address Books" },
                        ]}
                        value={addressBookMode}
                        onChange={setAddressBookMode}
                    />
                </div>
            )}
            {(isKonnektContext || addressBookMode === "legacy") && legacyTable}
            {!isKonnektContext && addressBookMode === "home" && (
                <AddressBookView
                    deviceId={user?.id}
                    canEdit
                    searchQuery={searchQuery}
                />
            )}
            {!isKonnektContext && addressBookMode === "shared" && (
                <SharedAddressBooksView
                    deviceId={user?.id}
                    searchQuery={searchQuery}
                />
            )}
            {legacyModals}
        </div>
    );
}

export default ContactsTab;